(() => {
  const body = document.body;
  const root = window.Shopify?.routes?.root || '/';

  const overlays = new WeakMap();

  function overlayFor(el) {
    if (!overlays.has(el)) overlays.set(el, new Overlay(el));
    return overlays.get(el);
  }

  function isReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /* ---------------------------------------------------------- */
  /* Shared quantity clamping (min / max / step)                 */
  /* ---------------------------------------------------------- */
  function quantityRules(container) {
    const min = parseInt(container.dataset.quantityMin, 10);
    const max = parseInt(container.dataset.quantityMax, 10);
    const step = parseInt(container.dataset.quantityStep, 10);
    return {
      min: Number.isFinite(min) ? min : 1,
      max: Number.isFinite(max) && max > 0 ? max : 999,
      step: Number.isFinite(step) && step > 0 ? step : 1,
    };
  }

  function clampQuantity(input, container) {
    const rules = quantityRules(container);
    let value = parseInt(input.value, 10);
    if (!Number.isFinite(value)) value = rules.min;
    if (rules.step > 1) {
      value = Math.round((value - rules.min) / rules.step) * rules.step + rules.min;
    }
    value = Math.min(rules.max, Math.max(rules.min, value));
    input.value = value;
    return value;
  }

  function bindQuantity(container) {
    const input = container.querySelector('[data-quantity-input]');
    if (!input) return;
    const step = quantityRules(container).step;

    const commit = () => clampQuantity(input, container);

    input.addEventListener('change', commit);

    container.querySelectorAll('[data-quantity-plus], [data-quantity-minus]').forEach((btn) => {
      btn.addEventListener('click', (event) => {
        event.preventDefault();
        const current = clampQuantity(input, container);
        const next = btn.hasAttribute('data-quantity-plus') ? current + step : current - step;
        input.value = Math.min(quantityRules(container).max, Math.max(quantityRules(container).min, next));
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
  }

  function emitAddToCart(data) {
    const analytics = window.BcosAnalytics;
    if (!analytics) return;
    const lines = Array.isArray(data) ? data : [data];
    const items = lines.map((line) => ({
      item_id: String(line.product_id || line.variant_id || ''),
      item_name: line.product_title || line.title || '',
      quantity: line.quantity,
      price: (line.price || 0) / 100,
      variant_id: String(line.variant_id || ''),
    }));
    analytics.track('add_to_cart', {
      currency: analytics.getContext()?.currency,
      value: items.reduce((sum, item) => sum + (item.price * item.quantity), 0),
      items,
    }, 'analytics');
  }

  function getFocusable(container) {
    return [...container.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )].filter((el) => el.offsetParent !== null || el === document.activeElement);
  }

  function trapFocus(container, event) {
    const focusable = getFocusable(container);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  class Overlay {
    constructor(el) {
      this.el = el;
      this.lastFocused = null;
    }

    open() {
      this.lastFocused = document.activeElement;
      this.el.hidden = false;
      requestAnimationFrame(() => this.el.setAttribute('data-open', 'true'));
      body.classList.add('bcos-scroll-lock');
      const focusTarget = this.el.querySelector('[data-bcos-dialog-autofocus], input');
      if (focusTarget && focusTarget.type !== 'hidden') focusTarget.focus();
      else if (this.el.querySelector('button')) this.el.querySelector('button').focus();
      this._keyHandler = (event) => {
        if (event.key === 'Escape') this.close();
        if (event.key === 'Tab') trapFocus(this.el, event);
      };
      document.addEventListener('keydown', this._keyHandler);
    }

    close() {
      this.el.setAttribute('data-open', 'false');
      document.removeEventListener('keydown', this._keyHandler);
      setTimeout(() => {
        this.el.hidden = true;
      }, isReducedMotion() ? 0 : 280);
      body.classList.remove('bcos-scroll-lock');
      if (this.lastFocused && this.lastFocused.isConnected) this.lastFocused.focus();
    }
  }

  function getCartElement() {
    return document.querySelector('[data-bcos-cart]');
  }

  function syncCartCount(count) {
    const value = Number(count || 0);
    const badge = document.querySelector('[data-bcos-cart-count]');
    if (!badge) return;
    badge.textContent = value;
    badge.hidden = value <= 0;
  }

  async function refreshCartCount() {
    try {
      const res = await fetch(`${root}cart.js`, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
      if (!res.ok) return;
      const cart = await res.json();
      syncCartCount(cart.item_count || 0);
    } catch (error) {
      console.warn('Cart count refresh failed', error);
    }
  }

  async function renderCart() {
    const drawer = getCartElement();
    if (!drawer) return;
    const wasOpen = drawer.getAttribute('data-open') === 'true';
    const sectionId = drawer.dataset.sectionId;
    const res = await fetch(`${root}cart?section_id=${sectionId}`, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
    const html = await res.text();
    const template = document.createElement('template');
    template.innerHTML = html.trim();
    const fresh = template.content.querySelector('[data-bcos-cart]');
    if (fresh) {
      drawer.replaceWith(fresh);
      bindCart();
      if (wasOpen) {
        fresh.hidden = false;
        requestAnimationFrame(() => fresh.setAttribute('data-open', 'true'));
      }
    }
    await refreshCartCount();
  }

  function openCartDrawer() {
    const drawer = getCartElement();
    if (!drawer) return;
    const overlay = overlayFor(drawer);
    overlay.open();
    window.BcosAnalytics?.track('view_cart', {}, 'analytics');
  }

  function setCartPending(pending) {
    const drawer = getCartElement();
    if (!drawer) return;
    drawer.classList.toggle('bcos-cart--pending', pending);
    const error = drawer.querySelector('[data-bcos-cart-error]');
    if (error) error.hidden = true;
  }

  function bindCart() {
    const drawer = getCartElement();
    if (!drawer) return;
    const overlay = overlayFor(drawer);

    drawer.querySelectorAll('[data-bcos-cart-close]').forEach((btn) => {
      btn.addEventListener('click', () => overlay.close());
    });

    async function updateQuantity(key, quantity) {
      const res = await fetch(`${root}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity }),
      });
      if (res.ok) {
        await renderCart();
      }
    }

    drawer.addEventListener('change', (event) => {
      const input = event.target.closest('[data-quantity-input]');
      if (input) {
        const line = input.closest('[data-cart-line]');
        if (line) updateQuantity(line.dataset.key, parseInt(input.value, 10) || 0);
      }
    });

    drawer.addEventListener('click', (event) => {
      const minus = event.target.closest('[data-quantity-minus]');
      const plus = event.target.closest('[data-quantity-plus]');
      const remove = event.target.closest('[data-cart-remove]');
      const line = event.target.closest('[data-cart-line]');
      if (!line) return;
      const input = line.querySelector('[data-quantity-input]');
      const current = parseInt(input.value, 10);
      if (minus) {
        event.preventDefault();
        updateQuantity(line.dataset.key, Math.max(0, current - 1));
      }
      if (plus) {
        event.preventDefault();
        updateQuantity(line.dataset.key, current + 1);
      }
      if (remove) {
        event.preventDefault();
        updateQuantity(line.dataset.key, 0);
      }
    });
  }

  /* ---------------------------------------------------------- */
  /* Predictive search                                           */
  /* ---------------------------------------------------------- */
  function bindSearch() {
    const panel = document.querySelector('[data-bcos-search]');
    if (!panel) return;
    const overlay = overlayFor(panel);
    const input = panel.querySelector('[data-bcos-search-input]');
    const results = panel.querySelector('[data-bcos-search-results]');
    let debounceTimer = null;

    panel.querySelectorAll('[data-bcos-search-close]').forEach((btn) => {
      btn.addEventListener('click', () => overlay.close());
    });

    if (input) {
      input.addEventListener('input', () => {
        clearTimeout(debounceTimer);
        const query = input.value.trim();
        if (query.length < 2) {
          results.innerHTML = '';
          return;
        }
        debounceTimer = setTimeout(() => fetchSuggestions(query), 180);
      });
    }

    async function fetchSuggestions(query) {
      const params = new URLSearchParams({
        q: query,
        'resources[type]': 'product,collection,article,page',
        'resources[limit]': '4',
        'resources[options][fields]': 'title,product_type,variants.title,vendor',
        'resources[options][unavailable_products]': 'hide',
      });
      try {
        const res = await fetch(`${root}search/suggest.json?${params.toString()}`, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
        if (!res.ok) throw new Error('suggest failed');
        const data = await res.json();
        renderResults(data.resources);
      } catch (error) {
        results.innerHTML = '';
      }
    }

    function formatMoney(cents) {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: window.Shopify?.currency?.active || 'USD',
      }).format(cents / 100);
    }

    function priceLabel(product) {
      if (product.price_min !== product.price_max) return `From ${formatMoney(product.price_min)}`;
      return formatMoney(product.price_min);
    }

    function escapeHtml(value) {
      return String(value).replace(/[&<>"']/g, (ch) => ({
        '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
      })[ch]);
    }

    function group(title, items) {
      if (!items || !items.length) return '';
      const rows = items.map((item) => {
        const image = item.featured_image?.url || item.image?.url || '';
        const label = escapeHtml(item.title || '');
        const link = item.url || '#';
        return `
          <li>
            <a class="bcos-sr__item" href="${link}">
              ${image
                ? `<img class="bcos-sr__thumb" src="${image}" alt="" width="56" height="56" loading="lazy">`
                : '<span class="bcos-sr__thumb bcos-sr__thumb--empty" aria-hidden="true"></span>'}
              <span class="bcos-sr__meta">
                <span class="bcos-sr__name">${label}</span>
                ${item.price ? `<span class="bcos-sr__price">${priceLabel(item)}</span>` : ''}
              </span>
            </a>
          </li>`;
      }).join('');
      return `
        <section class="bcos-sr__group" aria-label="${escapeHtml(title)}">
          <h3 class="bcos-sr__heading">${escapeHtml(title)}</h3>
          <ul class="bcos-sr__list" role="list">${rows}</ul>
        </section>`;
    }

    function renderResults(resources) {
      const html = [
        group('Products', resources?.products),
        group('Collections', resources?.collections),
        group('Articles', resources?.articles),
        group('Pages', resources?.pages),
      ].join('');

      if (!html.trim()) {
        results.innerHTML = `<p class="bcos-sr__none">No results for "${escapeHtml(input.value.trim())}".</p>`;
        return;
      }
      results.innerHTML = html;
    }
  }

  /* ---------------------------------------------------------- */
  /* Add to cart (AJAX)                                          */
  /* ---------------------------------------------------------- */
  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('[data-bcos-add-to-cart]');
    if (!form) return;
    event.preventDefault();
    const button = form.querySelector('[type="submit"]');
    if (button) button.disabled = true;
    const formData = new FormData(form);
    const extraItems = [...form.querySelectorAll('[data-bcos-extra-item]')];

    let bodyData;
    let contentType = 'application/x-www-form-urlencoded; charset=UTF-8';
    if (extraItems.length) {
      const entries = [...formData.entries()];
      const mainId = formData.get('id');
      const mainQuantity = formData.get('quantity') || '1';
      const properties = {};
      const items = [];
      entries.forEach(([key, value]) => {
        if (key === 'id' || key === 'quantity' || key === 'items[][id]' || key === 'items[][quantity]') return;
        if (key.startsWith('properties[')) {
          const name = key.slice(11, -1);
          properties[name] = value;
        }
      });
      if (mainId) items.push({ id: mainId, quantity: mainQuantity, properties });
      extraItems.forEach((extra) => {
        items.push({ id: extra.value, quantity: 1 });
      });
      bodyData = JSON.stringify({ items });
      contentType = 'application/json';
    } else {
      bodyData = new URLSearchParams(formData).toString();
    }

    try {
      setCartPending(true);
      openCartDrawer();
      const res = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': contentType },
        body: bodyData,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.description || 'Error');
      setCartPending(false);
      syncCartCount(data?.items?.reduce((total, item) => total + Number(item.quantity || 0), 0) || 0);
      await renderCart();
      emitAddToCart(data);
    } catch (error) {
      console.error('Add to cart failed', error);
      setCartPending(false);
      const drawer = getCartElement();
      const errorEl = drawer && drawer.querySelector('[data-bcos-cart-error]');
      if (errorEl) {
        errorEl.hidden = false;
        errorEl.textContent = error && error.message ? error.message : 'Could not add to cart.';
      }
      if (button) {
        button.disabled = false;
        button.textContent = 'Please try again';
      }
    }
  });

  /* ---------------------------------------------------------- */
  /* Bundle (multi-item add to cart)                             */
  /* ---------------------------------------------------------- */
  function bindBundle() {
    const form = document.querySelector('[data-bcos-bundle-form]');
    if (!form) return;

    const items = [...form.querySelectorAll('[data-bundle-item]')];
    const totalAmount = form.querySelector('[data-bundle-total-amount]');

    function updateTotal() {
      let total = 0;
      items.forEach((item) => {
        const select = item.querySelector('[data-bundle-variant-select]');
        const priceEl = select
          ? select.selectedOptions[0]
          : item.querySelector('[data-bundle-variant-id]');
        const price = parseInt(priceEl?.dataset.bundlePrice || '0', 10);
        const quantity = parseInt(item.querySelector('[data-quantity-input]')?.value || '1', 10);
        if (price) total += price * quantity;
      });
      if (totalAmount) totalAmount.textContent = window.Shopify?.formatMoney
        ? window.Shopify.formatMoney(total, window.Shopify?.moneyFormat)
        : `$${(total / 100).toFixed(2)}`;
    }

    form.addEventListener('change', updateTotal);
    updateTotal();

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      const button = form.querySelector('[data-bundle-submit]');
      if (button) button.disabled = true;
      const bodyData = new URLSearchParams(new FormData(form));
      try {
        setCartPending(true);
        openCartDrawer();
        const res = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8' },
          body: bodyData.toString(),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.description || 'Error');
        setCartPending(false);
        syncCartCount(data?.items?.reduce((total, item) => total + Number(item.quantity || 0), 0) || 0);
        await renderCart();
        emitAddToCart(data);
      } catch (error) {
        console.error('Add to cart failed', error);
        setCartPending(false);
        const drawer = getCartElement();
        const errorEl = drawer && drawer.querySelector('[data-bcos-cart-error]');
        if (errorEl) {
          errorEl.hidden = false;
          errorEl.textContent = error && error.message ? error.message : 'Could not add to cart.';
        }
        if (button) {
          button.disabled = false;
          button.textContent = 'Please try again';
        }
      }
    });
  }

  /* ---------------------------------------------------------- */
  /* Cart page (quantity updates)                                */
  /* ---------------------------------------------------------- */
  function bindCartPage() {
    const page = document.querySelector('[data-cart-page]');
    if (!page) return;
    const sectionId = page.closest('.shopify-section')?.id?.replace('shopify-section-', '');

    async function renderSection() {
      if (!sectionId) return;
      const res = await fetch(`${root}cart?section_id=${sectionId}`, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
      const html = await res.text();
      const template = document.createElement('template');
      template.innerHTML = html.trim();
      const fresh = template.content.querySelector('[data-cart-page]');
      if (fresh) {
        page.replaceWith(fresh);
        bindCartPage();
      }
    }

    async function updateLine(line, quantity) {
      const res = await fetch(`${root}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: line.dataset.key, quantity }),
      });
      if (res.ok) await renderSection();
    }

    page.addEventListener('change', (event) => {
      const input = event.target.closest('[data-quantity-input]');
      if (input) {
        const line = input.closest('[data-cart-line]');
        if (line) updateLine(line, parseInt(input.value, 10) || 0);
      }
    });

    page.addEventListener('click', (event) => {
      const button = event.target.closest('[data-quantity-minus], [data-quantity-plus]');
      if (!button) return;
      const line = button.closest('[data-cart-line]');
      if (!line) return;
      event.preventDefault();
      const input = line.querySelector('[data-quantity-input]');
      const current = parseInt(input.value, 10);
      const next = button.hasAttribute('data-quantity-plus') ? current + 1 : Math.max(0, current - 1);
      updateLine(line, next);
    });
  }

  function bindHeaderDisclosures() {
    document.querySelectorAll('.bcos-header__desktop-nav .bcos-header__disclosure').forEach((details) => {
      const closeIfIdle = () => {
        if (!details.contains(document.activeElement)) {
          details.removeAttribute('open');
        }
      };
      details.addEventListener('mouseenter', () => {
        details.setAttribute('open', '');
      });
      details.addEventListener('mouseleave', closeIfIdle);
      details.addEventListener('focusout', (event) => {
        if (!details.contains(event.relatedTarget)) {
          details.removeAttribute('open');
        }
      });
    });
  }

  function bindHeaderGroupScroll() {
    const groups = document.querySelectorAll('.shopify-section-group-header-group');
    if (!groups.length) return;
    const mobileMenu = document.querySelector('.bcos-header__mobile-menu');

    const reveal = () => {
      groups.forEach((group) => group.classList.remove('bcos-header-group--hidden'));
    };
    const hide = () => {
      groups.forEach((group) => group.classList.add('bcos-header-group--hidden'));
    };
    const isNearTop = () => window.scrollY < 240;
    const overlayOpen = () => document.querySelector('[data-open="true"]') !== null;

    let lastY = window.scrollY;
    let ticking = false;

    const update = () => {
      const y = window.scrollY;
      if (
        isNearTop() ||
        overlayOpen() ||
        (mobileMenu && mobileMenu.hasAttribute('open')) ||
        y <= lastY
      ) {
        reveal();
      } else if (y - lastY > 6) {
        hide();
      }
      lastY = y;
      ticking = false;
    };

    window.addEventListener('scroll', () => {
      if (!ticking) {
        ticking = true;
        window.requestAnimationFrame(update);
      }
    }, { passive: true });

    window.addEventListener('mousemove', (event) => {
      if (event.clientY < 140) reveal();
    }, { passive: true });

    document.addEventListener('focusin', (event) => {
      groups.forEach((group) => {
        if (group.contains(event.target)) reveal();
      });
    });
  }

  document.addEventListener('click', (event) => {
    const cartToggle = event.target.closest('[data-bcos-cart-toggle]');
    if (cartToggle) {
      event.preventDefault();
      const drawer = getCartElement();
      if (drawer) {
        const overlay = overlayFor(drawer);
        drawer.getAttribute('data-open') === 'true' ? overlay.close() : overlay.open();
      }
      return;
    }

    const searchToggle = event.target.closest('[data-bcos-search-toggle]');
    if (searchToggle) {
      event.preventDefault();
      const panel = document.querySelector('[data-bcos-search]');
      if (panel) {
        const overlay = overlayFor(panel);
        panel.getAttribute('data-open') === 'true' ? overlay.close() : overlay.open();
      }
    }
  });

  const localizationSelects = [...document.querySelectorAll('[data-bcos-localization-select]')];
  localizationSelects.forEach((select) => {
    select.addEventListener('change', () => {
      const form = select.closest('form[data-bcos-localization-form], form[id="BcosLocalization"]');
      if (form) form.submit();
    });
  });

  bindCart();
  bindSearch();
  bindCartPage();
  bindBundle();
  bindHeaderDisclosures();
  bindHeaderGroupScroll();
  document.querySelectorAll('[data-quantity]').forEach(bindQuantity);
})();
